import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Facility} from "types/Facility";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {ExclamationCircleIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import {formattedDate, secondsToHuman} from "services/date";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteFacility from "pages/Facility/Delete";
import {FullscreenControl, Layer, NavigationControl, Source} from "react-map-gl/mapbox";
import "services/mabox-gl";
import {Feature} from "geojson";
import {bounds} from "services/geometry";
import BooleanIndicator from "components/BooleanIndicator";
import {useOperator} from "context/OperatorContext";
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import {toTitleCase} from "services/string";
import Gallery from "components/Gallery";
import {ClockIcon, ExternalLinkIcon} from "@heroicons/react/outline";
import IconBadge from "components/IconBadge";
import IconName from "types/IconName";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import StatBox from "../../../components/StatBox";
import StatList from 'components/StatList';
import Map from "../../../components/Map";

// Helpers ----

function mapFeatures(facility?: Facility): { siteFeature?: Feature, zoneFeature?: Feature, facilityFeature?: Feature } {
  if (!facility) {
    return {siteFeature: undefined, zoneFeature: undefined, facilityFeature: undefined}
  }

  const siteFeature: Feature = {
    type: "Feature",
    id: facility.zone.site.id,
    properties: {name: facility.zone.site.name},
    geometry: facility.zone.site.area
  }

  const zoneFeature: Feature = {
    type: "Feature",
    id: facility.zone.id,
    properties: {name: facility.zone.name},
    geometry: facility.zone.area
  }

  const facilityFeature: Feature = {
    type: "Feature",
    id: facility.id,
    properties: {name: facility.name},
    geometry: facility.position
  }

  return {siteFeature, zoneFeature, facilityFeature}
}

// Component ----

function ViewFacility() {
  const {settings, termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const [deletingFacility, setDeletingFacility] = useState<Facility | null>(null);

  const {data: facility, isLoading} = useQuery(
    ['facility', {id}],
    () => ngage.facilities.get(Number(id)),
    {enabled: auth.can('facilities:read')}
  );

  const {siteFeature, zoneFeature, facilityFeature} = mapFeatures(facility)

  // Prepare action handlers
  function deleteFacility() {
    if (facility) setDeletingFacility(facility)
  }

  function editFacility() {
    if (id) navigate(`/facilities/${id}/edit`)
  }

  // Prevent unauthorised viewing
  if (auth.cannot('facilities:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('facilities:write') &&
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={deleteFacility}>Delete</Button>
      }

      {auth.can('facilities:write') &&
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={editFacility}>Edit</Button>
      }
    </>
  )

  const breadcrumbs = {
    "Locations": "/",
    [termFor('Facilities')]: "/facilities",
    [facility?.name || `View ${termFor('Facility')}`]: `/facilities/${id}`
  }

  // Prepare map
  const mapViewState = {
    bounds: facility ? bounds(facility.zone.site.area) : undefined,
  }

  return (
    <Page
      title={facility?.name || `View ${termFor('Facility')}`}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >
      {facility &&
        <>
          <section>
            <StatList>
              <StatBox
                name={'Avg. Dwell Duration'}
                icon={ClockIcon}
                stat={facility.average_dwell_duration ? secondsToHuman(facility.average_dwell_duration) : 'N/A'}
                tooltip={`This ${termFor('facility')} must be associated with a beacon in order to collect data.`}
              />
            </StatList>
          </section>

          {/* Facility Information */}
          <section className="mt-4">
            <Card
              heading={`${termFor('Facility')} Information`}
              subheading={`${toTitleCase(facility.type.name)} | Added on ${formattedDate(facility.created_at)}`}
            >
              <DescriptionList>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Location</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{facility.zone.name} ({facility.zone.site.name})</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Mapped</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={facility.is_mapped}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Destination</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={facility.is_destination}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Description</DescriptionTerm>
                  <DescriptionDetails>
                    <p
                      className="whitespace-pre-line">{facility.description ?? `A ${termFor('facility')} in ${termFor('zone')} '${facility.zone.name}'`}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Accessibility</DescriptionTerm>
                  <DescriptionDetails>
                    {!!facility.accessibility_criteria.length && Object.values(facility.accessibility_criteria).map((criterion, index) => (
                      <IconBadge icon={IconName.HAND} text={toTitleCase(criterion)} color={IconBadge.colors.BRAND} key={index}/>
                    ))}

                    {!facility.accessibility_criteria.length &&
                      <p>No criteria listed</p>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Sustainable</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={facility.is_sustainable}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Action</DescriptionTerm>
                  <DescriptionDetails>
                    {!!facility.action &&
                      <a href={facility.action.url} target="_blank" rel="noreferrer"
                         className="flex items-center font-medium text-brand hover:text-brand/80">
                        {facility.action.text} <ExternalLinkIcon className="h-4 w-4 text-gray-700 ml-1"/>
                      </a>
                    }

                    {!facility.action &&
                      <span>Open App (Default)</span>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Standard Hours</DescriptionTerm>
                  <DescriptionDetails>
                    {!!facility.hours &&
                      <StackedList>
                        {Object
                          .entries(facility.hours)
                          .filter(([day]) => !['overflow', 'exceptions'].includes(day))
                          .map(([day, ranges], index) => (
                              <StackedListItem key={index}>
                                <div className="flex justify-between w-full">
                                  <div className="font-semibold">{toTitleCase(day)}</div>
                                  <div>
                                    {Array.isArray(ranges) ? (ranges.length > 0 ? ranges.join(', ') : 'Closed') : 'Closed'}
                                  </div>
                                </div>
                              </StackedListItem>
                            )
                          )
                        }
                      </StackedList>
                    }

                    {!facility.hours &&
                      <p>Open when site is open</p>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          {/* Detailed Information */}
          {!!facility.information.length &&
            <section className="mt-4">
              <Card heading="Detailed Information">
                {facility.information.map((section, index) => (
                  <article key={index} className="mb-5">
                    <h3 className="text-base leading-6 font-medium text-gray-900 mb-2">{section.name}</h3>
                    <p className="text-sm whitespace-pre-line">{section.content}</p>
                  </article>
                ))}
              </Card>
            </section>
          }

          {/* Media */}
          {!!facility.media.length &&
            <section className="mt-4">
              <Card heading="Media">
                <Gallery media={facility.media}/>
              </Card>
            </section>
          }

          {/* Map */}
          <section className="mt-4 rounded-md shadow-sm overflow-hidden">
            <Map
              height='32rem'
              defaultMapStyle={settings?.audience_app.map_style ?? null}
              initialViewState={mapViewState}
              hidePointsOfInterest
            >
              <NavigationControl showCompass={true} visualizePitch={true}/>
              <FullscreenControl/>

              <Source id="site" type="geojson" data={siteFeature}>
                <Layer
                  id="site"
                  type="fill"
                  paint={{'fill-color': '#1d1956', 'fill-opacity': 0.5}}
                />
                <Layer
                  id="site-name"
                  type="symbol"
                  layout={{'text-field': ['get', 'name'], 'text-anchor': 'center'}}
                  paint={{'text-halo-color': '#ececec', 'text-halo-width': 2}}
                />
              </Source>

              <Source id="zone" type="geojson" data={zoneFeature}>
                <Layer
                  id="zone"
                  beforeId={"site-name"}
                  type="fill"
                  paint={{'fill-color': '#3730a3', 'fill-opacity': 0.5}}
                />
                <Layer
                  id="zone-name"
                  beforeId={"site-name"}
                  type="symbol"
                  layout={{'text-field': ['get', 'name'], 'text-anchor': 'center'}}
                  paint={{'text-color': '#ffffff', 'text-halo-color': '#282828', 'text-halo-width': 1}}
                />
              </Source>

              <Source id="facility" type="geojson" data={facilityFeature}>
                <Layer
                  id="facilities"
                  type="symbol"
                  layout={{
                    "icon-image": "facility-icon",
                    "icon-keep-upright": true,
                    "icon-size": 0.8,
                    "icon-offset": [0, -20],
                    "text-field": ["get", "name"],
                    "text-offset": [0, 0.25],
                    "text-anchor": "top"
                  }}
                  paint={{'text-halo-color': '#ececec', 'text-halo-width': 2}}
                />
              </Source>
            </Map>
          </section>

          {/* Notes */}
          {!!facility.notes.length &&
            <section className="mt-4">
              <Card heading="Notes">
                <ul className="space-y-8">
                  {facility.notes.map((note, index) => {
                    return (
                      <li key={index}>
                        <div className="flex space-x-3">
                          <div className="flex-shrink-0">
                            <img className="h-10 w-10 rounded-full"
                                 src={note.author.profile_photo_url}
                                 alt={note.author.name}/>
                          </div>
                          <div>
                            <div className="text-sm">
                              <span className="font-medium text-gray-500">
                                  {note.author.name}
                              </span>
                            </div>
                            <div className="mt-2 text-sm text-gray-700">
                              <h3 className="text-sm leading-6 font-medium text-gray-900 mb-1">{note.title}</h3>
                              <p className="whitespace-pre-line">{note.body}</p>
                            </div>
                            <div className="mt-2 text-sm flex items-center space-x-2">
                              {note.is_important &&
                                <ExclamationCircleIcon className="text-red-500 h-5 w-5"/>
                              }

                              <span className="text-gray-500 font-medium">
                                {note.created_at?.toRelative()}
                              </span>

                              {note.expires_at &&
                                <>
                                  <span className="text-gray-500 font-medium">·</span>
                                  <span className="text-gray-900 font-medium">
                                    {(note.is_expired ? 'Expired' : 'Expires') + ' ' + note.expires_at.toRelative()}
                                  </span>
                                </>
                              }
                            </div>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            </section>
          }
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteFacility
        show={facility === deletingFacility}
        facility={deletingFacility}
        onCancel={() => setDeletingFacility(null)}
        onDelete={facility => {
          navigate('/facilities')
          notify("success", `${termFor('Facility')} Deleted`, `${facility.name} has been permanently removed`)
        }}
        onDeleteFail={facility => {
          setDeletingFacility(null)
          notify("error", `${termFor('Facility')} Not Deleted`, `${facility.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewFacility;